import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import { Settings } from "../../settings";
import { isCoinSymbol, isJackpotSymbol, isBonusSymbol } from "../../SymbolData";
import { formatToCustomLocale } from "../../utilities";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";
import store from "../../State/Store/store";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import PotCollectionValueTraveler from "./PotCollectionValueTraveler";
import { sortGridOrder } from "./potCollectionGridOrder";

// ─── Symbol → Blank Variant Mapping (GDD §13.3) ─────────────────────────────

/**
 * After value collection, each symbol type swaps to its blank variant:
 * - Regular coins (c1=15, c_mini=17, c_minor=18, c_major=19) → coinbl (26)
 * - Jackpot coins (21=cm_minor, 22=cm_mini, 23=cm_major) → jpbl (27)
 * - Collect Zeus (cc=14) → ccbl (28)
 * - Double Zeus (cd=12) → cdbl (29)
 * - Mystery Zeus (cm=13) → already transformed to JP, uses jpbl (27)
 */
// eslint-disable-next-line no-unused-vars
const SYMBOL_TO_BLANK = {
  15: 26, // c1 → coinbl
  17: 26, // c_mini → coinbl
  18: 26, // c_minor → coinbl
  19: 26, // c_major → coinbl
  21: 27, // cm_minor → jpbl
  22: 27, // cm_mini → jpbl
  23: 27, // cm_major → jpbl
  12: 29, // cd → cdbl
  13: 27, // cm → jpbl (already JP after mystery reveal)
  14: 28, // cc → ccbl
  20: 29, // cd_mult → cdbl
};

// ─── Timing Constants ────────────────────────────────────────────────────────

const VALUE_TRAVEL_DURATION = 300;     // ms for trail travel (matches FeatureExecutor)
const COUNTER_ANIMATE_DURATION = 250;  // ms for counter text tick-up per coin

const TOTAL_CELLS = Settings.REEL_COUNT * Settings.SYMBOLS_VISIBLE; // 15

/**
 * PotCollectionEndOfBonusEngine
 *
 * Orchestrates the end-of-bonus value collection sequence:
 *
 * 1. Iterate grid horizontally: row-by-row (symbol index 0→2), left→right within each row
 * 2. For each coin: flip-reveal if jackpot unrevealed, REMOVE value (hide text),
 *    fire trail to coin_collector, on arrival play hit + increment counter
 * 3. After last coin: play hit_final on collector
 * 4. Grand Jackpot (if grid full)
 * 5. Max Win check
 * 6. Total Win popup
 *
 * Key difference from collect feature: values are TAKEN from coins (hidden),
 * not cloned. The original coin value disappears as the trail departs.
 *
 * GDD: §13.1 steps 18–27, §13.3, §14, §18.2, §19, §22.5
 */
class PotCollectionEndOfBonusEngine {
  /**
   * @param {Object} params
   * @param {Object} params.overlayReelRefs - PC overlay reel refs
   * @param {Object} params.reelRefs - PC static reel refs
   * @param {Object} params.collectionLayerRef - For value travelers
   * @param {Object} params.coinCollectorSpine - The coin_collector Spine instance
   * @param {Function} params.dispatch - Redux dispatch
   * @param {Function} params.onCollectionComplete - Called when all collection done
   * @param {number} params.symbolWidth
   * @param {number} params.symbolHeight
   * @param {number} params.reelSpacing
   */
  constructor({
    overlayReelRefs,
    reelRefs,
    collectionLayerRef,
    coinCollectorSpine,
    dispatch,
    onCollectionComplete,
    symbolWidth,
    symbolHeight,
    reelSpacing,
  }) {
    this.overlayReelRefs = overlayReelRefs;
    this.reelRefs = reelRefs;
    this.collectionLayerRef = collectionLayerRef;
    this.coinCollectorSpine = coinCollectorSpine;
    this.dispatch = dispatch;
    this.onCollectionComplete = onCollectionComplete;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    this._valueTraveler = null;
    this._cancelled = false;
    this._collectedTotal = 0;
    this._counterText = null;          // PIXI.Text on the coin collector
    this._counterAnimTicker = null;    // Running counter animation
    this._counterAnimResolve = null;   // Pending _animateCounter promise resolver
    this._counterSoundStarted = false; // Counter loop sound active
    this._pendingTimers = new Set();
    this._flippedCoins = new Set();    // Track flip-revealed coins
    this._slamStopRequested = false;   // Slam stop during collection
    this._collectionActive = false;    // True while collection loop is running
  }

  // ── Slam Stop ───────────────────────────────────────────────────────────────

  /**
   * Request slam stop during collection — remaining coins collect instantly.
   * Idempotent, safe to call multiple times.
   */
  requestSlamStop() {
    if (this._slamStopRequested) return;
    this._slamStopRequested = true;
  }

  // ── Grid Position Helper ──────────────────────────────────────────────────

  /**
   * Resolve the live PIXI.Container from the collectionLayerRef.
   * Always fresh — survives resize/recreation.
   */
  _getCollectionContainer() {
    return this.collectionLayerRef?.current?.getContainer?.() || null;
  }

  _getGridPosition(reelIndex, symbolIndex) {
    // Use overlay symbol's actual world position if available (responsive)
    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    const container = overlaySymbolRef?.getContainer?.();
    const collectionContainer = this._getCollectionContainer();

    if (container && collectionContainer && !collectionContainer.destroyed) {
      const globalPos = container.toGlobal({ x: 0, y: 0 });
      const localPos = collectionContainer.toLocal(globalPos);
      return { x: localPos.x, y: localPos.y };
    }

    // Fallback to math-based position
    const centerOffset = (Settings.REEL_COUNT - 1) / 2;
    const x = this.reelSpacing * (reelIndex - centerOffset);
    const containerY = -this.symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5);
    const y = containerY + this.symbolHeight * symbolIndex;
    return { x, y };
  }

  // ── Collector Target Position ─────────────────────────────────────────────

  /**
   * Get the coin collector's `collect_txt` bone position in collectionLayer local space.
   * This is where trails should land.
   */
  _getCollectorTargetPosition() {
    const spine = this.coinCollectorSpine;
    if (!spine || spine.destroyed) return { x: 0, y: this.symbolHeight * 2 };

    const collectionContainer = this._getCollectionContainer();

    // Find the collect_txt bone
    const bone = spine.skeleton?.findBone?.("collect_txt");
    if (bone && collectionContainer && !collectionContainer.destroyed) {
      // Get bone's world position
      bone.updateWorldTransform();
      const globalPos = spine.toGlobal({ x: bone.worldX, y: bone.worldY });
      const localPos = collectionContainer.toLocal(globalPos);
      return { x: localPos.x, y: localPos.y };
    }

    // Fallback: approximate position below the grid
    if (collectionContainer && !collectionContainer.destroyed) {
      const globalPos = spine.toGlobal({ x: 0, y: 0 });
      const localPos = collectionContainer.toLocal(globalPos);
      return { x: localPos.x, y: localPos.y };
    }

    return { x: 0, y: this.symbolHeight * 2 };
  }

  // ── Counter Text Initialization ───────────────────────────────────────────

  /**
   * Create the running total PIXI.Text and inject it into the coin collector's
   * txt_slot_collector_value slot. Uses FONT_STYLES.winText (same as GVC coin counter).
   * The slot follows bone transforms automatically and fades with landscape_out.
   */
  _initCounterText() {
    const spine = this.coinCollectorSpine;
    if (!spine || spine.destroyed) return;

    const slotName = "txt_slot_collector_value";
    const slot = spine.skeleton?.findSlot?.(slotName);
    if (!slot) {

      return;
    }

    // Size the font as a fraction of the collector's native height.
    // Since the text is injected via addSlotObject, it inherits the same bone
    // transforms as the collector art. Using the spine's design-space height
    // as the reference keeps text proportional to the collector on every device.
    const nativeHeight = spine.skeleton.data.height || 100;
    const multiplier = FEATURES_CONFIG.POT_COLLECTION.COLLECTOR_FONT_SIZE_MULTIPLIER ?? 0.3;
    const fontSize = nativeHeight * multiplier;

    // Cache total scale for autoFit overflow compression
    spine.update(0);
    const valueBone = spine.skeleton.findBone("collector_value");
    const boneBaseScale = Math.abs(valueBone?.a) || 1;
    this._counterTotalScale = (Math.abs(spine.scale.x) || 1) * boneBaseScale;

    const style = new PIXI.TextStyle(scaleStyle(FONT_STYLES.winText, fontSize));
    this._counterText = new PIXI.Text("0", style);
    this._counterText.anchor.set(0.5);
    this._counterText.resolution = 2;
    this._counterText.roundPixels = true;

    // Wrap in container for slot injection
    this._counterTextContainer = new PIXI.Container();
    this._counterTextContainer.name = "collector_value_text";
    this._counterTextContainer.addChild(this._counterText);

    // Hidden until first coin arrives — don't show "0"
    this._counterText.alpha = 0;

    // Inject into the spine's text slot — follows bone transforms and
    // inherits slot rgba (fades out with landscape_out automatically)
    spine.addSlotObject(slotName, this._counterTextContainer);
  }

  /**
   * Auto-fit the counter text: if text overflows available width,
   * compress horizontally only (squish X, preserve height).
   * Same pattern as PotCollectionOverlaySymbolController.autoFitCoinValueText.
   */
  _autoFitCounterText() {
    if (!this._counterText || this._counterText.destroyed) return;

    // Reset scale before measuring
    this._counterText.scale.set(1);
    this._counterText.updateText();

    const totalScale = this._counterTotalScale || 1;
    const paddingPercent = 0.08;
    const maxTextWidth = totalScale !== 0
      ? (this.symbolWidth * (1 - paddingPercent * 2)) / totalScale
      : this.symbolWidth * (1 - paddingPercent * 2);
    const actualTextWidth = this._counterText.width;

    if (actualTextWidth > maxTextWidth) {
      const scaleRatio = maxTextWidth / actualTextWidth;
      // Squish X only — preserve height for readability
      this._counterText.scale.x = scaleRatio;
    }
  }

  // ── Main Collection Flow ──────────────────────────────────────────────────

  /**
   * Execute the end-of-bonus value collection.
   * Called after the coin collector enters idle.
   *
   * @param {boolean} [slamStop=false] - Skip animations
   * @returns {Promise<void>}
   */
  async runCollection(slamStop = false) {
    // Prevent re-entry — once collection runs, it never runs again
    if (this._hasCompleted) {

      return;
    }
    if (this._cancelled) return;

    const state = store.getState();
    const coinCount = state.potCollection?.coinCount || 0;
    const isGridFull = coinCount >= TOTAL_CELLS;
    const coinValues = state.potCollection?.currentRound?.storage?.values
      || state.potCollection?.initialRespinValues
      || [];

    // Initialize value traveler for trail FX
    const container = this._getCollectionContainer();
    if (container) {
      this._valueTraveler = new PotCollectionValueTraveler(
        container, this.symbolWidth, this.symbolHeight
      );
    } else {

    }

    // Initialize counter text on collector
    this._initCounterText();
    this._collectedTotal = 0;
    this._isGridFull = isGridFull; // GJP trail follows if true — last EOB coin isn't truly final

    // Collect all coin values left→right, one-by-one
    await this._collectAllValues(coinValues, slamStop);
    if (this._cancelled) return;

    // Mark as completed — prevents any re-run
    this._hasCompleted = true;

    // Signal completion — SpinEngine orchestrates GJP/total win as next steps
    this.onCollectionComplete?.(this._collectedTotal);
  }

  // ── Value Collection ──────────────────────────────────────────────────────

  async _collectAllValues(coinValues, slamStop) {
    if (this._cancelled) return;

    const state = store.getState();
    const currentReels = state.potCollection?.currentRound?.reels
      || state.potCollection?.initialRespinScreen;

    if (!currentReels) return;

    const bet = this._getBet();

    // Build ordered list of collectible coins: horizontal (row-by-row, left→right)
    const unordered = [];
    for (let ri = 0; ri < Settings.REEL_COUNT; ri++) {
      const reel = currentReels[ri];
      if (!Array.isArray(reel)) continue;

      for (let si = 0; si < Settings.SYMBOLS_VISIBLE; si++) {
        let symbolId = parseInt(reel[si], 10);
        if (isNaN(symbolId)) continue;

        // Only collect coins, jackpots, and Zeus coins
        if (!isCoinSymbol(symbolId) && !isJackpotSymbol(symbolId) && !isBonusSymbol(symbolId)) {
          continue;
        }

        // Resolve actual visual symbol ID from overlay — by EOB all zeus are transformed to c/j
        const overlayReelRef = this.overlayReelRefs?.current?.[ri];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(si);
        const ctrl = overlaySymbolRef?.getController?.();
        if (ctrl) {
          const visualId = ctrl.currentSymbolId;
          if (visualId != null && visualId !== symbolId) {
            symbolId = visualId;
          }
        }

        const rawValue = coinValues?.[ri]?.[si] || 0;
        // Values are already in subunits — no bet multiplication needed
        const displayValue = rawValue;
        if (displayValue <= 0) continue;

        unordered.push({
          reelIndex: ri,
          symbolIndex: si,
          symbolId,
          rawValue,
          displayValue,
        });
      }
    }
    const collectibles = sortGridOrder(unordered);

    // Step 0: Flip ALL unrevealed jackpot coins upfront — before any collection.
    // Resolves at value_ready so it's fast; darken will handle skin change later.
    const flipPromises = [];
    for (const coin of collectibles) {
      if (this._cancelled) break;
      const overlayReelRef = this.overlayReelRefs?.current?.[coin.reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(coin.symbolIndex);
      const ctrl = overlaySymbolRef?.getController?.();
      if (ctrl?.activeSpineKey === "j" && !(ctrl?.coinValueText?.text)) {
        flipPromises.push(this._flipIfNeeded(coin.reelIndex, coin.symbolIndex, coin.rawValue, coin.symbolId));
      }
    }
    if (flipPromises.length > 0) {
      await Promise.all(flipPromises);
    }

    // Process coins — sequential normally, all-at-once for slam/turbo
    this._collectionActive = true;

    // Max win cap (GDD §18.2): collection stops once total hits 10,000× bet
    const maxWinMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
    const maxWin = maxWinMultiplier * bet;

    // Check if we should start in fast mode (turbo) or detect mid-collection slam
    if (slamStop) {
      // ── FAST MODE: fire ALL trails simultaneously (turbo/slam) ──
      await this._collectAllAtOnce(collectibles, maxWin);
    } else {
      // ── SEQUENTIAL MODE: one coin at a time, with slam stop check ──
      // Cache the trail TextStyle once per run — fontSize is constant during
      // collection (mirrors _collectAllAtOnce). Consumed by _fireCoinTrail
      // instead of allocating a new TextStyle per coin.
      const seqFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      this._cachedTrailTextStyle = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, seqFontSize));

      for (let i = 0; i < collectibles.length; i++) {
        if (this._cancelled) return;

        // Max win reached — stop collecting further coins
        if (this._collectedTotal >= maxWin) {
          break;
        }

        // Mid-collection slam stop → fire remaining coins simultaneously
        if (this._slamStopRequested) {
          await this._collectAllAtOnce(collectibles.slice(i), maxWin);
          break;
        }

        const coin = collectibles[i];
        const isLast = i === collectibles.length - 1;

        // Clamp coin value so total doesn't exceed max win
        const cappedValue = Math.min(coin.displayValue, maxWin - this._collectedTotal);
        const coinForTrail = cappedValue !== coin.displayValue
          ? { ...coin, displayValue: cappedValue }
          : coin;

        // Step 1: REMOVE value from coin (hide it — value is TAKEN, not cloned)
        this._hideSourceCoinValue(coin.reelIndex, coin.symbolIndex);

        // Step 2: Fire trail from coin → collector
        const prevTotal = this._collectedTotal;
        this._collectedTotal += cappedValue;

        await this._fireCoinTrail(coinForTrail, prevTotal, this._collectedTotal, isLast);
        if (this._cancelled) return;

        // Step 3: Darken coin in background (fire-and-forget — don't wait)
        this._darkenAndSwap(coin.reelIndex, coin.symbolIndex, coin.symbolId, false);

        // Stop collecting if we've hit the cap
        if (this._collectedTotal >= maxWin) {
          break;
        }
      }
    }

    // Release the cached trail style so a later run at a different size rebuilds it
    this._cachedTrailTextStyle = null;

    this._collectionActive = false;

  }

  // ── Fast Mode: All Trails Simultaneously ──────────────────────────────────

  /**
   * Fire all coin trails at once — used by turbo and slam stop.
   * Same pattern as collect/double FAST MODE: no cascading delay.
   */
  async _collectAllAtOnce(coins, maxWin = Infinity) {
    if (this._cancelled || !this._valueTraveler) return;

    // All coins already flipped upfront in runCollection — go straight to trails

    // Step 1: Counter stays hidden (alpha=0) until first trail arrives — don't show "0"

    // Step 2: Fire all trails with 1-frame stagger (spreads PIXI.Text creation across frames)
    // Each coin's value text is hidden individually when its trail clones it (seamless handoff).
    let arrivedCount = 0;
    let lastCounterPromise = null;
    const trailPromises = [];

    // Cache TextStyle once per collection run — fontSize is identical for all coins
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const textStyle = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));

    // Filter to only coins within the cap, clamping the last one if needed
    const cappedCoins = [];
    let running = this._collectedTotal;
    for (const coin of coins) {
      if (running >= maxWin) {
        break;
      }
      const cappedValue = Math.min(coin.displayValue, maxWin - running);
      cappedCoins.push(cappedValue !== coin.displayValue ? { ...coin, displayValue: cappedValue } : coin);
      running += cappedValue;
    }

    // Play trail sound once for the entire batch (not per-coin)
    soundManager.play(SOUND_NAME.particles_fly);

    for (let i = 0; i < cappedCoins.length; i++) {
      if (this._cancelled) break;

      const coin = cappedCoins[i];
      const fromPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);
      // Resolve collector target fresh per-coin — survives resize/orientation change
      const toPos = this._getCollectorTargetPosition();

      const displayText = formatToCustomLocale(coin.displayValue);

      // Scale ratio
      let scaleTo = 0.5;
      const collContainer = this._getCollectionContainer();
      if (this._counterText && !this._counterText.destroyed && collContainer) {
        try {
          const halfH = this._counterText.height / 2;
          const gTop = this._counterText.toGlobal({ x: 0, y: -halfH });
          const gBot = this._counterText.toGlobal({ x: 0, y: halfH });
          const globalCounterH = Math.abs(gBot.y - gTop.y);
          const tTop = collContainer.toGlobal({ x: 0, y: -fontSize / 2 });
          const tBot = collContainer.toGlobal({ x: 0, y: fontSize / 2 });
          const globalTrailH = Math.abs(tBot.y - tTop.y);
          if (globalTrailH > 0) scaleTo = globalCounterH / globalTrailH;
        } catch (e) { /* fallback */ }
      }

      // Hide source value text at the moment the trail clones it — seamless handoff
      this._hideSourceCoinValue(coin.reelIndex, coin.symbolIndex);

      trailPromises.push(this._valueTraveler.travelWithTrail({
        text: displayText,
        style: textStyle,
        from: fromPos,
        to: toPos,
        duration: VALUE_TRAVEL_DURATION,
        scaleFrom: 1,
        scaleTo,
        // eslint-disable-next-line no-loop-func
        onArrival: () => {
          arrivedCount++;

          // Show counter on first arrival (not before — avoids showing "0")
          if (arrivedCount === 1 && this._counterText && this._counterText.alpha === 0) {
            this._counterText.alpha = 1;
          }

          // Update running total on each arrival
          this._collectedTotal += coin.displayValue;
          const isLastArrival = arrivedCount === cappedCoins.length;
          lastCounterPromise = this._animateCounter(
            this._collectedTotal - coin.displayValue,
            this._collectedTotal,
            isLastArrival
          );

          // Play hit on collector ONLY on final arrival — avoids N rapid restarts in fast mode
          if (arrivedCount === cappedCoins.length) {
            this._playCollectorHit(true);
          }

          // Darken coin on arrival — play animated darken (fire-and-forget, same as sequential)
          this._darkenAndSwap(coin.reelIndex, coin.symbolIndex, coin.symbolId, false);

        },
      }));

      // Yield 1 frame between trail launches to spread work visually
      if (i < cappedCoins.length - 1) {
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
    }

    // Wait for all trails to complete
    await Promise.all(trailPromises);

    // Wait for the last coin's counter tick + end sound before GJP can start
    if (lastCounterPromise) {
      await lastCounterPromise;
    }

  }

  // ── Single Coin Trail ─────────────────────────────────────────────────────

  async _fireCoinTrail(coin, prevTotal, newTotal, isLast) {
    if (this._cancelled || !this._valueTraveler) return;

    const fromPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);
    const toPos = this._getCollectorTargetPosition();

    // Reuse cached style if available (set by _collectAllAtOnce / runCollection),
    // otherwise create one (standalone _fireCoinTrail calls)
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const textStyle = this._cachedTrailTextStyle
      || new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));

    const displayText = formatToCustomLocale(coin.displayValue);

    // Compute dynamic scale ratio: shrink from coin text size → collector counter size
    // Counter text and trail text are in different coordinate hierarchies, so we must
    // measure both in global/screen space for an accurate ratio.
    let scaleTo = 0.5; // sensible fallback
    const collContainer = this._getCollectionContainer();
    if (this._counterText && !this._counterText.destroyed && collContainer) {
      try {
        // Counter text's actual screen height (includes spine scale, bone transform, etc.)
        const halfH = this._counterText.height / 2;
        const gTop = this._counterText.toGlobal({ x: 0, y: -halfH });
        const gBot = this._counterText.toGlobal({ x: 0, y: halfH });
        const globalCounterH = Math.abs(gBot.y - gTop.y);

        // Trail text's screen height (in collection layer space — no spine scaling)
        const tTop = collContainer.toGlobal({ x: 0, y: -fontSize / 2 });
        const tBot = collContainer.toGlobal({ x: 0, y: fontSize / 2 });
        const globalTrailH = Math.abs(tBot.y - tTop.y);

        if (globalTrailH > 0) {
          scaleTo = globalCounterH / globalTrailH;
        }
      } catch (e) {
        // fallback stays 0.5
      }
    }

    soundManager.play(SOUND_NAME.particles_fly);
    let counterPromise = null;
    await this._valueTraveler.travelWithTrail({
      text: displayText,
      style: textStyle,
      from: fromPos,
      to: toPos,
      duration: VALUE_TRAVEL_DURATION,
      scaleFrom: 1,
      scaleTo,
      onArrival: () => {
        // Show counter text on first coin arrival
        if (this._counterText && this._counterText.alpha === 0) {
          this._counterText.alpha = 1;
        }
        // Play hit animation on collector (last coin gets hit_final)
        this._playCollectorHit(isLast);
        // Animate counter from prevTotal to newTotal
        counterPromise = this._animateCounter(prevTotal, newTotal, isLast);
      },
    });
    if (counterPromise) {
      await counterPromise;
    }
  }

  // ── Collector Hit Animation ───────────────────────────────────────────────

  _playCollectorHit(isLast = false) {
    const spine = this.coinCollectorSpine;
    if (!spine || spine.destroyed || !spine.state) return;

    // hit_final only on the truly last addition to the collector.
    // If grid is full, a GJP trail follows — so the last EOB coin is NOT final.
    const isTrulyFinal = isLast && !this._isGridFull;

    // Play impact sound when coin arrives at collector
    soundManager.play(SOUND_NAME.particles_end);

    try {
      const anim = isTrulyFinal ? "hit_final" : "hit";
      spine.state.setAnimation(0, anim, false);
      spine.state.addAnimation(0, "idle", true, 0);
    } catch (err) {

    }
  }

  // ── Counter Text Animation ────────────────────────────────────────────────

  _animateCounter(fromValue, toValue, isFinal = false) {
    // Cancel any running counter animation
    if (this._counterAnimTicker) {
      PIXI.Ticker.shared.remove(this._counterAnimTicker);
      this._counterAnimTicker = null;
      if (this._counterAnimResolve) {
        this._counterAnimResolve();
        this._counterAnimResolve = null;
      }
    }

    if (!this._counterText || this._counterText.destroyed) return Promise.resolve();

    return new Promise((resolve) => {
      this._counterAnimResolve = resolve;

      // Start counter loop sound ONCE per phase — restarts after board counter_end
      if (!this._counterSoundStarted) {
        soundManager.play(SOUND_NAME.counter);
        this._counterSoundStarted = true;
      }

      let elapsed = 0;
      const duration = COUNTER_ANIMATE_DURATION;

      this._counterAnimTicker = () => {
        if (this._cancelled || !this._counterText || this._counterText.destroyed) {
          PIXI.Ticker.shared.remove(this._counterAnimTicker);
          this._counterAnimTicker = null;
          soundManager.stop(SOUND_NAME.counter);
          this._counterSoundStarted = false;
          if (this._counterAnimResolve) {
            this._counterAnimResolve();
            this._counterAnimResolve = null;
          }
          return;
        }

        elapsed += PIXI.Ticker.shared.deltaMS;
        const t = Math.min(1, elapsed / duration);
        const easedT = t * (2 - t); // easeOutQuad
        const currentValue = Math.round(fromValue + (toValue - fromValue) * easedT);
        this._counterText.text = formatToCustomLocale(currentValue);

        if (t >= 1) {
          PIXI.Ticker.shared.remove(this._counterAnimTicker);
          this._counterAnimTicker = null;
          // End this counter phase — board coins or GJP trail
          if (isFinal) {
            soundManager.stop(SOUND_NAME.counter);
            soundManager.play(SOUND_NAME.counter_end);
            this._counterSoundStarted = false;
          }
          if (this._counterAnimResolve) {
            this._counterAnimResolve();
            this._counterAnimResolve = null;
          }
        }
      };

      PIXI.Ticker.shared.add(this._counterAnimTicker);
    });
  }

  _updateCounterText(value) {
    if (!this._counterText || this._counterText.destroyed) return;
    this._counterText.text = formatToCustomLocale(value);
  }

  // ── Value Removal (TAKE, don't clone) ─────────────────────────────────────

  /**
   * Hide the source coin's value text — the value is being TAKEN from the coin.
   * Unlike the collect feature which clones, end-of-bonus removes the value.
   */
  _hideSourceCoinValue(reelIndex, symbolIndex) {
    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    const controller = overlaySymbolRef?.getController?.();

    if (controller?.coinValueText && !controller.coinValueText.destroyed) {
      controller.coinValueText.alpha = 0;
    }
    if (controller?.coinValueContainer && !controller.coinValueContainer.destroyed) {
      controller.coinValueContainer.alpha = 0;
    }
  }

  // ── Flip Reveal (same pattern as FeatureExecutor) ─────────────────────────

  /**
   * Flip-reveal a jackpot coin (j spine) to show its value.
   * Resolves at value_ready so collection proceeds immediately.
   * Complete handler sets default skin + idle in the background.
   * Darken is handled by the collection path, not here.
   */
  async _flipIfNeeded(reelIndex, symbolIndex, rawValue, symbolId) {
    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) return;

    const controller = overlaySymbolRef.getController?.();

    // Only flip jackpot coins ("j" spine) that don't yet have visible text
    if (controller?.activeSpineKey !== "j") return;
    const alreadyRevealed = !!(controller?.coinValueText?.text);
    if (alreadyRevealed) return;

    // Guard: only flip once
    const flipKey = `${reelIndex}_${symbolIndex}`;
    if (this._flippedCoins.has(flipKey)) return;
    this._flippedCoins.add(flipKey);

    // Inject value text into slot with alpha 0 — it follows bone transforms during flip
    this._updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, rawValue);
    if (controller.coinValueText) controller.coinValueText.alpha = 0;
    if (controller.coinValueContainer) controller.coinValueContainer.alpha = 0;

    const spine = controller?.getActiveSpine?.();
    if (spine?.state) {
      // Start flip, show text after 1 frame
      soundManager.play(SOUND_NAME.j_rotate);
      spine.state.setAnimation(0, "double_value_rotate", false);
      await new Promise(r => requestAnimationFrame(r));
      if (controller.coinValueText) controller.coinValueText.alpha = 1;
      if (controller.coinValueContainer) controller.coinValueContainer.alpha = 1;

      await new Promise((resolve) => {
        let resolved = false;
        let flipSafetyTid = null;
        const listener = {
          event: (entry, event) => {
            if (event.data.name === "value_ready") {
              safeResolve();
            }
          },
          complete: (entry) => {
            if (entry.animation.name === "double_value_rotate") {
              // Background cleanup: set default skin + idle
              if (spine?.skeleton && spine?.state) {
                spine.skeleton.setSkinByName("default");
                spine.skeleton.setSlotsToSetupPose();
                spine.state.setAnimation(0, "idle", true);
              }
              safeResolve();
            }
          },
        };
        const safeResolve = () => {
          if (resolved) return;
          resolved = true;
          if (flipSafetyTid != null) {
            flipSafetyTid.clear();
            this._pendingTimers.delete(flipSafetyTid);
          }
          try { spine.state.removeListener(listener); } catch (_) {}
          resolve();
        };
        try {
          spine.state.addListener(listener);
          // Safety fallback — tracked for cleanup on cancel()
          flipSafetyTid = new TickerTimeout(safeResolve, 3000);
          this._pendingTimers.add(flipSafetyTid);
        } catch (err) {

          safeResolve();
        }
      });
    }
  }

  /**
   * Set value text on overlay symbol (mirrors FeatureExecutor._updateOverlayValueText).
   */
  _updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, rawValue) {
    const controller = overlaySymbolRef?.getController?.();
    if (!controller) return;

    const displayValue = formatToCustomLocale(rawValue);

    if (controller.coinValueText && !controller.coinValueText.destroyed) {
      // Text already exists — just update
      controller.coinValueText.text = displayValue;
      controller.coinValueText.alpha = 1;
      if (controller.coinValueContainer && !controller.coinValueContainer.destroyed) {
        controller.coinValueContainer.alpha = 1;
      }
    } else {
      // Text doesn't exist (mystery-only games: jackpot coins never had value
      // injected by double/collect features). Create it now.

      const result = controller.createCoinValueText(displayValue);
      if (!result) return;

      controller.coinValueContainer = result.counterContainer;
      controller.coinValueText = result.text;

      // Attach to the spine's text slot
      const spineInstance = controller.getActiveSpine();
      controller._coinValueSpine = spineInstance;

      if (spineInstance) {
        const slotName = controller._getTextSlotName(controller.currentSymbolId);
        if (slotName) {
          const spineScale = spineInstance.scale.x || 1;
          const slot = spineInstance.skeleton?.findSlot(slotName);
          const slotBone = slot?.bone;
          const boneBaseScale = Math.abs(slotBone?.a) || 1;
          const totalScale = spineScale * boneBaseScale;

          if (totalScale && totalScale !== 0) {
            const baseFontSize = controller.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
            const scaledFontSize = baseFontSize / totalScale;
            const baseStyle = FONT_STYLES.coinValue;
            Object.assign(controller.coinValueText.style, scaleStyle(baseStyle, scaledFontSize));
            controller.coinValueText.scale.set(1);

            const maxTextWidth = (controller.symbolWidth * (1 - 0.05 * 2)) / totalScale;
            if (controller.coinValueText.width > maxTextWidth) {
              const scaleRatio = maxTextWidth / controller.coinValueText.width;
              controller.coinValueText.scale.x = scaleRatio;
            }
          }

          spineInstance.addSlotObject(slotName, controller.coinValueContainer);
        }
      }
    }
  }

  // ── Darken + Swap ─────────────────────────────────────────────────────────

  async _darkenAndSwap(reelIndex, symbolIndex, symbolId, slamStop) {
    if (this._cancelled) return;

    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    const controller = overlaySymbolRef?.getController?.();
    const activeSpine = controller?.getActiveSpine?.();

    // Play darken animation on whatever spine is currently active
    // After darken, the coin stays visible in its darkened state (frozen on last frame).
    // We do NOT swap to blank or hide the overlay.
    // DO NOT call setSlotsToSetupPose — the j spine's value display relies on
    // the reveal animation's final frame state; resetting slots destroys that.
    if (activeSpine?.state) {
      // Ensure default skin before darken on jackpot (j) spines only —
      // flip may not have completed yet (resolves at value_ready, not complete).
      // Do NOT touch cs/other spines — they don't have a 'default' skin.
      if (controller?.activeSpineKey === 'j' && activeSpine.skeleton?.skin?.name && activeSpine.skeleton.skin.name !== 'default') {
        activeSpine.skeleton.setSkinByName('default');
        activeSpine.skeleton.setSlotsToSetupPose();
      }
      const darkenAnims = this._findDarkenAnimation(activeSpine, symbolId);
      if (darkenAnims) {
        if (slamStop) {
          // Instant: jump to darken last frame
          try {
            const entry = activeSpine.state.setAnimation(0, darkenAnims, false);
            // Jump to end of animation so it shows the final darkened frame
            if (entry) entry.trackTime = entry.animationEnd;
          } catch (err) {

          }
        } else {
          // Animated: play darken once, freeze on last frame
          await new Promise((resolve) => {
            let tid = null;
            let darkenListener = null;
            const done = () => {
              if (tid) { tid.clear(); this._pendingTimers.delete(tid); tid = null; }
              if (darkenListener) {
                try { activeSpine.state.removeListener(darkenListener); } catch (_) {}
                darkenListener = null;
              }
              resolve();
            };
            try {
              activeSpine.state.setAnimation(0, darkenAnims, false);
              darkenListener = {
                complete: (entry) => {
                  if (entry.animation.name === darkenAnims) done();
                },
              };
              activeSpine.state.addListener(darkenListener);
              tid = new TickerTimeout(done, 1500);
              this._pendingTimers.add(tid);
            } catch (err) {
              done();
            }
          });
        }
      }
    }
  }

  /**
   * Find the appropriate darken animation for the current spine/symbolId.
   */
  _findDarkenAnimation(spine, symbolId) {
    // Try specific darken animations first
    const DARKEN_MAP = {
      21: "CM_Red/jp_transform_red_cm_minor_darken",
      22: "CM_Red/jp_transform_red_cm_mini_darken",
      23: "CM_Red/jp_transform_red_cm_major_darken",
      12: "darken_blue_cc_green_cd",
      14: "darken_blue_cc_green_cd",
    };

    const preferredAnim = DARKEN_MAP[symbolId];
    if (preferredAnim) {
      try {
        const animData = spine.skeleton?.data?.findAnimation?.(preferredAnim);
        if (animData) return preferredAnim;
      } catch (_) { /* not found */ }
    }

    // Try generic darken
    try {
      const generic = spine.skeleton?.data?.findAnimation?.("darken");
      if (generic) return "darken";
    } catch (_) { /* not found */ }

    // Try darken_blue_cc_green_cd as fallback for Zeus coins
    if (isBonusSymbol(symbolId)) {
      try {
        const fallback = spine.skeleton?.data?.findAnimation?.("darken_blue_cc_green_cd");
        if (fallback) return "darken_blue_cc_green_cd";
      } catch (_) { /* not found */ }
    }

    return null;
  }

  // ── Utility ───────────────────────────────────────────────────────────────

  _getBet() {
    const state = store.getState();
    const freeRoundsActive = state?.freeRounds?.status === "active";
    const freeRoundsBet = state?.freeRounds?.betValue || null;
    return freeRoundsActive && freeRoundsBet
      ? freeRoundsBet
      : state.game.bet || 1;
  }

  _delay(ms) {
    if (this._cancelled) return Promise.resolve();
    return new Promise((resolve) => {
      const tid = new TickerTimeout(() => {
        this._pendingTimers.delete(tid);
        resolve();
      }, ms);
      this._pendingTimers.add(tid);
    });
  }

  resize(symbolWidth, symbolHeight, reelSpacing) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;
    if (this._valueTraveler) {
      this._valueTraveler.resize(symbolWidth, symbolHeight);
    }
    // Counter text is injected into txt_slot_collector_value via addSlotObject
    // — no manual bone sync needed, spine handles positioning automatically
  }

  cancel() {
    this._cancelled = true;

    // Clear all pending timers
    for (const tid of this._pendingTimers) {
      if (tid && typeof tid.clear === "function") tid.clear();
    }
    this._pendingTimers.clear();

    // Cancel counter animation
    if (this._counterAnimTicker) {
      PIXI.Ticker.shared.remove(this._counterAnimTicker);
      this._counterAnimTicker = null;
      if (this._counterAnimResolve) {
        this._counterAnimResolve();
        this._counterAnimResolve = null;
      }
    }

    if (this._valueTraveler) this._valueTraveler.cancel();
  }

  destroy() {
    this.cancel();

    if (this._valueTraveler) {
      this._valueTraveler.destroy();
      this._valueTraveler = null;
    }

    if (this._counterTextContainer && this.coinCollectorSpine) {
      this.coinCollectorSpine.removeSlotObject("txt_slot_collector_value", this._counterTextContainer);
    }
    if (this._counterText && !this._counterText.destroyed) {
      this._counterText.destroy();
    }
    if (this._counterTextContainer && !this._counterTextContainer.destroyed) {
      this._counterTextContainer.destroy({ children: true });
    }
    this._counterText = null;
    this._counterTextContainer = null;

    this.overlayReelRefs = null;
    this.reelRefs = null;
    this.collectionLayerRef = null;
    this.coinCollectorSpine = null;
    this.dispatch = null;
  }
}

export default PotCollectionEndOfBonusEngine;
